var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/** @jsx jsx */
import { Input as BSInput } from 'reactstrap';
import { React, jsx, css, classNames, INTERACTIVE_CLASS, hooks, focusElementInKeyboardMode } from 'jimu-core';
import { LoadingType } from './types';
import { Button } from './button';
import { Loading } from './loading';
import { fetchTextInputValidityResult } from '../utils/utils';
import { Popper } from './overlay';
import defaultMessage from '../translations/default';
import { withStyles } from 'jimu-theme';
import { CloseOutlined } from 'jimu-icons/outlined/editor/close';
import { WarningOutlined } from 'jimu-icons/outlined/suggested/warning';
/**
 * Use native input dom check method to check validity.
 */
const TypesWithInputDomCheckValidity = ['email'];
/**
 * The `TextInput` component provides the user with a text field to input string content.
 *
 * ```ts
 * import { TextInput } from 'jimu-ui'
 * ```
 */
export const _TextInput = React.forwardRef(function TextInputComponent(props, ref) {
    const { onAcceptValue, onPressEnter, value, className, style, prefix, suffix, checkValidityOnChange, checkValidityOnAccept, borderless, allowClear = false, size = 'default' } = props, otherProps = __rest(props, ["onAcceptValue", "onPressEnter", "value", "className", "style", "prefix", "suffix", "checkValidityOnChange", "checkValidityOnAccept", "borderless", "allowClear", "size"]);
    const isControlled = React.useMemo(() => value != null, [value]);
    const [isValid, setIsValid] = React.useState(true); // by default input is valid
    const [isBusy, setIsBusy] = React.useState(false);
    const [hasFocus, setHasFocus] = React.useState(false);
    const [hasContent, setHasContent] = React.useState((value != null && value !== '') || (props.defaultValue != null && props.defaultValue !== ''));
    const errorMsg = React.useRef();
    const cancelable = hooks.useCancelablePromiseMaker();
    const refObj = React.useRef();
    const handleRef = hooks.useForkRef(refObj, ref);
    const getI18nMessage = hooks.useTranslation(defaultMessage);
    // Only show validating result after user changes something.
    const shouldShowValidResult = React.useRef(false);
    // validate the value once when the component has been mounted
    React.useEffect(() => {
        let checkFunc;
        if (typeof checkValidityOnChange === 'function' && value) {
            checkFunc = checkValidityOnChange;
        }
        else if (typeof checkValidityOnAccept === 'function' && value) {
            checkFunc = checkValidityOnAccept;
        }
        if (checkFunc) {
            setIsBusy(true);
            fetchTextInputValidityResult(value, checkFunc)
                .then(checkResult => {
                if (!checkResult.valid) {
                    errorMsg.current = checkResult.msg;
                    shouldShowValidResult.current = true;
                    setIsValid(false);
                }
            })
                .finally(() => { setIsBusy(false); });
        }
    }, []); //eslint-disable-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (!props.defaultValue) {
            setHasContent((value != null && value !== ''));
        }
    }, [value, props.defaultValue]);
    const onChange = (evt) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, evt);
        let checkResult;
        const value = evt.target.value;
        if (typeof checkValidityOnChange === 'function') { // custom validation
            checkResult = yield cancelable(fetchTextInputValidityResult(value, checkValidityOnChange));
        }
        else if (props.required || TypesWithInputDomCheckValidity.includes(props.type)) {
            checkResult = { valid: refObj.current.checkValidity(), msg: getI18nMessage('requiredField') };
        }
        if (checkResult != null) {
            errorMsg.current = checkResult.msg;
            shouldShowValidResult.current = true;
            setIsValid(checkResult.valid);
        }
        setHasContent(value !== '');
    });
    const onAccept = (text) => __awaiter(this, void 0, void 0, function* () {
        setIsBusy(true);
        let checkResult;
        if (typeof checkValidityOnAccept === 'function') {
            checkResult = yield cancelable(fetchTextInputValidityResult(text, checkValidityOnAccept));
        }
        else if (props.required || TypesWithInputDomCheckValidity.includes(props.type)) {
            checkResult = { valid: refObj.current.checkValidity(), msg: getI18nMessage('requiredField') };
        }
        if (checkResult != null) {
            errorMsg.current = checkResult.msg;
            shouldShowValidResult.current = true;
            setIsValid(checkResult.valid);
        }
        if (!checkResult || checkResult.valid) {
            onAcceptValue === null || onAcceptValue === void 0 ? void 0 : onAcceptValue(text);
        }
        setIsBusy(false);
    });
    const onBlur = (evt) => {
        onAccept(evt.target.value).finally(() => {
            var _a;
            (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, evt);
        });
        setHasFocus(false);
    };
    const onFocus = (evt) => {
        var _a;
        (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props, evt);
        setHasFocus(true);
    };
    const onKeyUp = (evt) => {
        var _a;
        if (evt.key === 'Enter') {
            onAccept(evt.currentTarget.value).finally(() => {
                onPressEnter === null || onPressEnter === void 0 ? void 0 : onPressEnter(evt);
            });
        }
        (_a = props.onKeyUp) === null || _a === void 0 ? void 0 : _a.call(props, evt);
    };
    const setFocus = (evt) => {
        evt.stopPropagation();
        if (!hasFocus) {
            focusElementInKeyboardMode(refObj.current, true);
        }
    };
    const clearText = () => {
        var _a;
        refObj.current.value = '';
        setHasContent(false);
        // mock a change event
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, {
            target: refObj.current,
            currentTarget: refObj.current,
            value: '',
            preventDefault: () => null,
            stopPropagation: () => null
        });
        focusElementInKeyboardMode(refObj.current, true);
    };
    const classes = classNames('jimu-input', props.size && `jimu-input-${props.size}`, className, {
        'is-valid': shouldShowValidResult.current && isValid,
        'is-invalid': shouldShowValidResult.current && !isValid,
        'no-border': props.borderless,
        readonly: props.readOnly,
        disabled: props.disabled || isBusy
    });
    const getStyle = () => {
        return css `
      .input-wrapper {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        position: relative;
      }
      input {
        flex-grow: 1;
        border: none;
        padding: 0;
        outline: none;
        color: inherit;
        min-width: 0;
        background: transparent;
        &:focus,
        &:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      }
      .clear-content {
        cursor: pointer;
      }
    `;
    };
    return (jsx("div", { className: classes, css: getStyle(), style: style },
        jsx("span", { className: `input-wrapper ${INTERACTIVE_CLASS}` },
            prefix && jsx("span", { className: 'text-input-prefix mr-1' }, prefix),
            jsx(BSInput, Object.assign({}, otherProps, { className: 'text-truncate', size: props.htmlSize, bsSize: size === 'default' ? undefined : size, value: isControlled ? value : undefined, onChange: onChange, onBlur: onBlur, valid: shouldShowValidResult.current && isValid, invalid: shouldShowValidResult.current && !isValid, onFocus: onFocus, onKeyUp: onKeyUp, innerRef: handleRef })),
            allowClear && hasContent && (jsx(Button, { className: 'text-input-clear clear-content ml-1', type: 'tertiary', "aria-label": getI18nMessage('clear'), title: getI18nMessage('clear'), icon: true, size: 'sm', onClick: clearText },
                jsx(CloseOutlined, { size: 's' }))),
            shouldShowValidResult.current && !isValid && (jsx("div", { onClick: setFocus },
                jsx(WarningOutlined, { color: 'var(--sys-color-error-main)', css: css `min-width: 16px;` }))),
            suffix && jsx("span", { className: 'text-input-suffix ml-1' }, suffix),
            isBusy && (jsx(Loading, { type: LoadingType.Donut, width: 16, height: 16, css: css `
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
            ` }))),
        shouldShowValidResult.current && !isValid && errorMsg.current &&
            jsx(Popper, { open: hasFocus, placement: 'top-start', reference: refObj.current, trapFocus: false, autoFocus: false, zIndex: 2001, offsetOptions: 10 },
                jsx("div", { css: css `
                color: var(--ref-palette-black);
                background-color: var(--ref-palette-neutral-600);
                border-radius: 2px;
                padding: 2px 8px;
              ` }, errorMsg.current))));
});
/**
 * The `TextInput` component provides the user with a text field to input string content.
 *
 * ```ts
 * import { TextInput } from 'jimu-ui'
 * ```
 */
export const TextInput = withStyles(_TextInput, 'TextInput');
